import { FC, useContext, useRef, useMemo, useState, useEffect } from 'react'
import clsx from 'clsx'
import dynamic from 'next/dynamic'

import Auth from 'store/auth'
import ButtonFAQ from './button-faq/button-faq'
import WatchCheckup from './player/watch-checkup/watch-checkup'
import WebinarChat from './webinar-chat/webinar-chat'
import WebinarSocketContext from 'context/webinarSocketContext'
import TranslationSelector from './translation-selector/translation-selector'
import SwitchToggle from 'components/parts/switch-toggle/switch-toggle'
import VideoBlockInfo from '../video-block/video-block-info'
import BtnClose from 'public/img/filters/icon_close.svg'
const BannerAuthAccess = dynamic(
  () => import('components/banners/banner-auth-access/banner-auth-access')
)
const RatingTable = dynamic(() => import('../webinar-active/star-rating/rating-table/rating-table'))
import OnboardingWrap from 'containers/onboarding-wrap/onboarding-wrap'
import useTrackCheckup from './useTrackCheckup'
import useNMOWatched from './useNMOWatched'
import useLayoutAbsolute from 'store/common/useLayoutAbsolute'
import useDeviceOrientation, { ScreenOrientation } from 'hooks/useDeviceOrientation'

import { StatusVocabulary } from 'constants/data'
import { getAxiosSingle } from 'pages/api/axios'
import { scenarioName } from 'constants/onboarding'
import { IMaterial, ILectorsData } from 'constants/types/webinar.types'
import { Events } from 'constants/events'
import { nestApiPath } from 'constants/url'
import { webinarsForStudents } from 'components/students/students-data'
import { A_LOG_REG_HASH, originRegWebinars } from 'utils/analytics/other/registration/registration'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import {
  A_wa_openActiveWebinar,
  originActiveWebinar,
  originRaiting,
} from 'utils/analytics/webinars/webinat_active'

import styles from './webinar-active.module.scss'

interface IWebinar {
  lectorsData: ILectorsData
  dataset: IMaterial
}

const options = ['ОПИСАНИЕ', 'ЧАТ']

const WebinarActive: FC<IWebinar> = ({ dataset, lectorsData }: IWebinar) => {
  const { sendConfirm, checkup, webinarStatus, viewers } = useContext(WebinarSocketContext)
  const { isFilledUser, isSponsor, isAuthorized } = Auth.useAuth()
  const isWebinarForStudents = webinarsForStudents.includes(dataset.id)
  const { setLayoutStatus } = useLayoutAbsolute()
  const orientation = useDeviceOrientation()

  const [togglePosition, setTogglePosition] = useState(options[1])
  const [toggleVPN, setVPN] = useState(false)
  const [heightWithBars, setHeightWithBars] = useState(0)
  const videoRef = useRef<HTMLDivElement>(null)

  useTrackCheckup()
  useNMOWatched(dataset.id, webinarStatus, dataset.NMOTimeLength)

  const isEnded = webinarStatus === StatusVocabulary.PAST
  const isHourBefore = webinarStatus === StatusVocabulary.HOUR_BEFORE
  const isReminderAvailable = useMemo(
    () => !lectorsData.lectors.every((item) => item.subscribed),
    [lectorsData]
  )
  const isHorizontalOrientation = orientation === ScreenOrientation.horizontal
  const isChatVisible = isHorizontalOrientation || (!isEnded && togglePosition === options[1])
  const isAutoHeight =
    (((!isFilledUser && !isSponsor) || isEnded) && !isWebinarForStudents) || !isAuthorized
  const isRatingBtnShow = !isEnded && !isHourBefore
  const closeVpn = () => setVPN(false)

  const Info = useMemo(
    () => (
      <VideoBlockInfo
        dataset={dataset}
        viewers={viewers}
        lectorsData={lectorsData}
        isEnded={isEnded}
        isTranslationPage={true}
        origin={originShare.WEBINAR_ACTIVE}
      >
        {isRatingBtnShow && <RatingTable webinarId={dataset.id} origin={originRaiting.ACTIVE} />}
      </VideoBlockInfo>
    ),
    [dataset, viewers, lectorsData, isEnded, isRatingBtnShow]
  )

  useEffect(() => {
    setLayoutStatus(isChatVisible)
    return () => {
      setLayoutStatus(false)
    }
  }, [isChatVisible])

  useEffect(() => {
    const handleResize = () => {
      let innerHeight = window.innerHeight

      if (window.innerHeight < innerHeight) {
        innerHeight = window.innerHeight
      }
      setHeightWithBars(innerHeight)
    }

    const resultVpn = async () => {
      if (isEnded) return
      const isRussia = await getAxiosSingle(`${nestApiPath}/user/location`)
      if (!isRussia) setVPN(true)
    }
    A_wa_openActiveWebinar(originActiveWebinar.NOORIGIN, dataset.id)
    resultVpn()
    handleResize()
    window.addEventListener(Events.RESIZE, handleResize)
    return () => window.removeEventListener(Events.RESIZE, handleResize)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (!heightWithBars) return
      if (togglePosition === options[0] && window.innerHeight > heightWithBars) {
        document.documentElement.style.height = '100vh'
      } else document.documentElement.style.height = 'initial'
    }
    handleResize()
  }, [heightWithBars, togglePosition])

  return (
    <div
      className={clsx(styles.webinarContainer, {
        [styles.webinarContainerChat]: !isChatVisible,
      })}
    >
      <div className={styles.container}>
        <div className={clsx({ [styles.webinarLimiter]: !isEnded })}>
          {toggleVPN && isAuthorized && (
            <div className={styles.vpnInfo} onClick={closeVpn}>
              <p> Если у вас включен VPN, трансляция может отображаться некорректно </p>
              <BtnClose className={styles.closeVpn} />
            </div>
          )}
          <div
            className={clsx(styles.webinar, {
              [styles.webinarNotFilled]: isAutoHeight,
              [styles.webinarAutoHeight]: isAutoHeight,
              [styles.canClick]: !isAuthorized,
            })}
            ref={videoRef}
          >
            <TranslationSelector
              dataset={dataset}
              isEnded={isEnded}
              isForStudents={isWebinarForStudents}
            />
            {!isAuthorized && (
              <div
                className={styles.bannerWrapper}
                onClick={() => sessionStorage.setItem(A_LOG_REG_HASH, originRegWebinars.LOGIN)}
              >
                <BannerAuthAccess txt="Чтобы посмотреть вебинар" />
              </div>
            )}
          </div>
        </div>
        <div className={styles.infoHorizontal}>{Info}</div>
      </div>

      <div className={styles.chat}>
        {!isEnded && (
          <div className={styles.toggle}>
            <SwitchToggle
              options={options}
              handleToggle={setTogglePosition}
              selectedOption={togglePosition}
              modificator={styles.toggleOption}
              wrapperModificator={styles.toggleContent}
              lineModificator={styles.line}
              fadeModificator={styles.toggleFade}
              isWithoutFade={true}
            />
            <div className={styles.buttonFaq}>
              <ButtonFAQ />
            </div>
          </div>
        )}

        {isChatVisible && (
          <div className={styles.chatInner}>
            <WebinarChat isReminderAvailable={isReminderAvailable} />
          </div>
        )}

        {(togglePosition === options[0] || isEnded) && (
          <div className={styles.infoVertical}>{Info}</div>
        )}
      </div>
      <WatchCheckup checkup={checkup} handleShowConfirm={sendConfirm} />
      <OnboardingWrap configName={scenarioName.ACTIVE_WEBINAR_SCENARIO} />
    </div>
  )
}

export default WebinarActive
